/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { TextField, Divider, Grid, Box } from '@mic3/platform-ui';

import SelectionList from 'app/containers/Common/SelectionList/SelectionList';
import Loader from 'app/components/atoms/Loader/Loader';

import { addWorkspaceUsers, removeWorkspaceUsers } from 'store/actions/admin/workspacesActions';
import { loadUserWorkspaces } from 'store/actions/admin/userManagementAction';

import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { getPermissions } from 'app/config/rolesConfig';
import Immutable from 'app/utils/immutable/Immutable';
import { isEmpty, getStr } from 'app/utils/utils';


const SearchField = styled(TextField)`
    padding: 8px 16px !important;
`;

const StyledDivider = styled(Divider)`
    margin-bottom: 15px !important;
`;

const StyledGrid = styled(Grid)`
    margin: 0 8px;
    position: sticky !important;
    top: 0;
    background: ${({ theme }) => theme.material.colors.background.default} !important;
    z-index: 999;
`;

const SelectionListWrapper = styled(Box)`
    .MuiExpansionPanelSummary-root.Mui-expanded {
        min-height: 0 !important;
    }

    .MuiExpansionPanelDetails-root {
        margin-top: 0 !important;
        padding-right: 5px !important;
        padding-bottom:0 !important;
    }
    .button-wrapper{
        margin-right: 10px;
    }
`;

class UserWorkspaces extends PureComponent {
    static propTypes = {
        user: PropTypes.object, // user details
        isLoading: PropTypes.bool,
        loadUserWorkspaces: PropTypes.func,
        addWorkspaceUsers: PropTypes.func,
        removeWorkspaceUsers: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = Immutable({
            searchValue: '',
        });
        this.defaultState = this.state;
    }

    @bind
    async loadUserWorkspaces() {
        const { user } = this.props;
        await this.props.loadUserWorkspaces(user?.id);
    }

    componentDidUpdate(prevProps) {
        const { user: { id: prevId } = {} } = prevProps || {};
        const { user: { id: newId } = {} } = this.props || {};
        if (prevId !== newId) {
            this.loadUserWorkspaces();
        }
    }

    @bind
    setDefaultState() {
        this.setState(this.defaultState);
    }

    @bind
    onSearch(event) {
        this.setState({
            searchValue: event.target.value,
        });
    }

    @bind
    searchForFrom(searchQuery, data) {
        if (!data || !searchQuery) return false;
        return ['name', 'username', 'id'].some(field => {
            let value = getStr(data, field, '');
            if (!value) return false;
            value = value.toLowerCase();
            const query = searchQuery.toLowerCase();
            return value.includes(query);
        });
    }

    @bind
    @memoize()
    filterSearchedData(searchQuery, workspaces) {
        return {
            workspacesFiltered: searchQuery ? (workspaces || []).filter(item => this.searchForFrom(searchQuery, item)) : workspaces || [],
        };
    }

    @bind
    async onSubmitWorkspaces(newWorkspaces) {
        const { user: { id: userId } = {}, workspaces: existingWorkspaces, addWorkspaceUsers } = this.props || {};
        if (!userId || isEmpty(newWorkspaces)) {
            return;
        }
        const addedPromises = newWorkspaces.map(async workspace => {
            if (!existingWorkspaces.find(t => t.id === workspace.id)) {
                await addWorkspaceUsers(workspace.id, [{ userId, role: workspace.role }]);
            }
        }, []);
        await Promise.all(addedPromises).then(() => {
            this.loadUserWorkspaces();
            this.setDefaultState();
        });
    }

    @bind
    async removeWorkspaces(selectedWorkspaceListIds) {
        const { user: { id: userId } = {}, removeWorkspaceUsers } = this.props || {};
        if (!userId || isEmpty(selectedWorkspaceListIds)) {
            return;
        }
        const removePromises = selectedWorkspaceListIds.map(async workspaceId => {
            await removeWorkspaceUsers(workspaceId, [userId]);
        });
        await Promise.all(removePromises).then(() => {
            this.loadUserWorkspaces();
            this.setDefaultState();
        });
    }
    
    @bind
    async updateRole(role, data) {
        const {user, addWorkspaceUsers } = this.props;
        if (role === data.role || !data?.id || !user?.id) {
            return;
        }
        const { id: userId } = user;
        const payload = [{ role, userId }];
        await addWorkspaceUsers(data.id, payload); 
        this.loadUserWorkspaces();
    }

    @bind
    @memoize()
    normalizeWorkspaceAccess(workspaces) {
        const { loggedInUserProfile } = this.props;
        const { isAdmin, id } = loggedInUserProfile || {};
        if(!workspaces?.length) return [];
        return workspaces.map(w => {
            const isOwner = w.owners.some(o => o.id === id)
            return {...w, disabled: !isAdmin && !isOwner }
        });
    }

    render() {
        const { user, workspaces, isLoading } = this.props || {};
        const { canEdit } = getPermissions(user.role);
        const normalizedWorkspaces = this.normalizeWorkspaceAccess(workspaces); 
        const { searchValue } = this.state;
        const { workspacesFiltered } = this.filterSearchedData(searchValue, normalizedWorkspaces);
        const filterBy = [{ field: 'active', op: '=', value: true }];

        return (
            <>
                {isLoading && <Loader absolute backdrop />}
                <StyledGrid>
                    <SearchField
                        fullWidth
                        variant="standard"
                        margin="none"
                        placeholder="Search..."
                        InputProps={{ disableUnderline: true }}
                        value={searchValue}
                        onChange={this.onSearch}
                    />
                    {[...(this.props.sidebarActions || [])]}
                    <StyledDivider />
                </StyledGrid>
                <SelectionListWrapper>
                    <SelectionList
                        filteredList={workspacesFiltered}
                        list={workspaces}
                        type="workspace"
                        onAdd={this.onSubmitWorkspaces}
                        onRemove={this.removeWorkspaces}
                        isLoading={isLoading}
                        showRoles
                        userRole={user.role}
                        disabled={!canEdit }
                        setRole={this.updateRole}
                        filterBy={filterBy}
                        fullHeight
                    />
                </SelectionListWrapper>
            </>
        );
    }
}

export default connect(
    state => ({
        isLoading: state.admin.users.workspaces.isLoading,
        workspaces: state.admin.users.workspaces.records,
        loggedInUserProfile: state.user.profile,
    }),
    {
        loadUserWorkspaces,
        addWorkspaceUsers,
        removeWorkspaceUsers,
    }
)(UserWorkspaces);
